// src/components/DeskClocksPanel.js
//
// Master list item 6 (Bob 2026-10-14): on BOTH officers' desks - the clocks running on the desk's claims (the carrier's
// answer, the appeal ladder, the Board), the claims before a Board (and any shown finished while there), and the claims
// whose member withdrew her submission. The rows are computed by src\data\deskClocks.js (proved by
// scripts\test-desk-clocks.mjs); this renders them and adds nothing.
import React from 'react';
import { activate } from '../data/a11yActivate';   // pass 2: role, tabIndex and Enter/Space from one tested helper

const C = { panel: '#0B1626', line: '#2E4A6B', dim: '#8AA4BF', accent: '#E8A020', bad: '#E06C6C', ok: '#5FB878', text: '#DCE7F2' };

function Block({ title, subtitle, tone, count, children }) {
  const colour = tone === 'bad' ? C.bad : tone === 'warn' ? C.accent : C.ok;
  return (
    <div style={{ border: '1px solid ' + C.line, background: C.panel, marginBottom: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '10px 14px', borderBottom: '1px solid ' + C.line, gap: 12 }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>{title}</div>
          {subtitle ? <div style={{ fontSize: 11, color: C.dim, marginTop: 2 }}>{subtitle}</div> : null}
        </div>
        <div style={{ fontSize: 12, fontWeight: 700, color: colour, whiteSpace: 'nowrap' }}>{count}</div>
      </div>
      <div style={{ padding: '8px 14px 12px', fontSize: 12, color: C.dim, lineHeight: 1.55 }}>{children}</div>
    </div>
  );
}

const nameOf = (c) => (c && (c.id || c.docId)) || '(no claim number)';
const PARTY = { carrier: "the Carrier's time", organization: "the Organization's time" };

export default function DeskClocksPanel({ clocks, board, withdrawn, onOpen }) {
  const overdue = (clocks || []).filter((r) => r.left < 0).length;
  const open = (id) => onOpen && id && onOpen(id);
  return (
    <>
      <Block title="Clocks running on these claims" tone={overdue ? 'bad' : (clocks || []).length ? 'warn' : 'ok'}
             count={(clocks || []).length + ' clock(s)' + (overdue ? ' - ' + overdue + ' past due' : '')}
             subtitle="The carrier's answer, the appeal ladder and the Board - each from the agreement's own clock and the acts recorded on the claim. Past due first.">
        {(clocks || []).length === 0 ? 'No clock is running on a claim here. A claim not yet served on the carrier has no carrier clock; one with no catalogued agreement has none to show.' : (clocks || []).map((r, i) => (
          <div key={i} {...activate(() => open(r.claimId))} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '6px 0', borderBottom: '1px dotted ' + C.line, cursor: 'pointer' }}>
            <div>
              <span style={{ color: C.text }}>{r.title}</span>
              {PARTY[r.party] ? <div style={{ fontSize: 11, color: C.dim }}>{PARTY[r.party]}</div> : null}
            </div>
            <div style={{ fontSize: 11, whiteSpace: 'nowrap', color: r.left < 0 ? C.bad : r.left <= 10 ? C.accent : C.dim }}>
              {r.left < 0 ? 'past due ' + Math.abs(r.left) + 'd (' + r.date + ')' : r.left + 'd left (' + r.date + ')'}
            </div>
          </div>
        ))}
      </Block>

      <Block title="Before a Board" tone={(board && board.misreported.length) ? 'bad' : (board && board.before.length) ? 'warn' : 'ok'}
             count={((board && board.before.length) || 0) + ' claim(s)'}
             subtitle="Read from each claim's own appeal acts (a notice of intent or submission to a Board, an award not yet complied with). A claim closed on the property while it is before a Board is flagged - it is not finished.">
        {!(board && board.before.length) ? 'No claim here is before a Board by its recorded acts.' : board.before.map((r, i) => {
          const flagged = board.misreported.indexOf(r) !== -1;
          return (
            <div key={i} {...activate(() => open(r.claim.docId || r.claim.id))} style={{ padding: '6px 0', borderBottom: '1px dotted ' + C.line, cursor: 'pointer' }}>
              <span style={{ color: C.text }}>{nameOf(r.claim)}</span>
              <span style={{ color: C.dim }}>{r.claim.claimantName ? '  -  ' + r.claim.claimantName : ''}</span>
              <div style={{ fontSize: 11, color: flagged ? C.bad : C.dim }}>{flagged ? 'SHOWN CLOSED ON THE PROPERTY, BUT BEFORE A BOARD - ' : ''}{r.label}</div>
            </div>
          );
        })}
      </Block>

      <Block title="Withdrawn by the member" tone={(withdrawn || []).length ? 'warn' : 'ok'} count={(withdrawn || []).length + ' claim(s)'}
             subtitle="The member pulled her submission to the union. The union may still file the same claim itself while the filing window is open - the window is shown for that reason.">
        {(withdrawn || []).length === 0 ? 'No member has withdrawn a submission here.' : withdrawn.map((r, i) => (
          <div key={i} {...activate(() => open(r.claim.docId || r.claim.id))} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '6px 0', borderBottom: '1px dotted ' + C.line, cursor: 'pointer' }}>
            <div>
              <span style={{ color: C.text }}>{nameOf(r.claim)}</span>
              <span style={{ color: C.dim }}>{r.claim.claimantName ? '  -  ' + r.claim.claimantName : ''}</span>
              <div style={{ fontSize: 11, color: C.dim }}>withdrawn {r.since ? r.since.slice(0, 10) : '(date not recorded)'}</div>
            </div>
            <div style={{ fontSize: 11, whiteSpace: 'nowrap', color: r.windowLeft === null ? C.dim : r.windowLeft < 0 ? C.bad : C.accent }}>
              {r.windowLeft === null ? 'no filing window on record' : r.windowLeft < 0 ? 'window closed ' + r.windowCloses : r.windowLeft + 'd of window left - the union may still file'}
            </div>
          </div>
        ))}
      </Block>
    </>
  );
}
